/**
 * Shared numeric constants for the Reportes v3 libs (Plagas · Costes · Campañas).
 *
 * The severity weights/ranks and the freshness/reports factors are the SAME
 * numbers `src/lib/health/farmHealthModel.ts` uses for the Mapa de sanidad —
 * `farmHealthModel.ts` exports `SEV_WEIGHT`/`SEV_RANK` and a unit test asserts
 * equality so the map and the three reports can never disagree.
 */

/** Severity levels stored in `pest_scouting_sessions.severity` (null allowed). */
export type SeverityLevel = "low" | "medium" | "high" | "critical";

/** Ordered severity levels, lowest first. */
export const SEVERITY_LEVELS: readonly SeverityLevel[] = ["low", "medium", "high", "critical"] as const;

/** Severity → pressure weight (0..1). Mirrors farmHealthModel.SEV_WEIGHT. */
export const SEV_WEIGHT: Record<SeverityLevel, number> = { low: 0.35, medium: 0.6, high: 0.85, critical: 1 };

/** Weight used when a session carries no severity (most worker reports). */
export const SEV_WEIGHT_UNKNOWN = 0.45;

/** Severity → ordinal rank (higher = worse). Mirrors farmHealthModel.SEV_RANK. */
export const SEV_RANK: Record<SeverityLevel, number> = { low: 1, medium: 2, high: 3, critical: 4 };

/** Freshness half-life in days: weight halves every 7 days since the last report. */
export const FRESHNESS_HALF_LIFE_DAYS = 7;

/** Freshness never decays below this floor (an old focus still counts a little). */
export const FRESHNESS_FLOOR = 0.15;

/** Reports factor = min(REPORTS_FACTOR_CAP, 1 + REPORTS_FACTOR_SLOPE · ln(reports)). */
export const REPORTS_FACTOR_CAP = 1.6;
export const REPORTS_FACTOR_SLOPE = 0.25;

/** Inferred treatment response: first same-block spray within this many days after the report. */
export const RESPONSE_WINDOW_DAYS = 21;

/** Rebound («rebrote»): another same block+pest report within this many days after the response spray. */
export const REBOUND_WINDOW_DAYS = 21;

/**
 * «Volví y ya no hay»: una REVISIÓN LIMPIA de un foco. Se guarda como un parte
 * descartado con este motivo, pero NO es un descarte: nadie se equivocó al
 * reportar. No cuenta como falsa alarma de quien la hizo ni en el pie de
 * «descartados» — solo forma parte de la historia de su foco.
 */
export const CLEAN_CHECK_REASON = "clean_check";

/** Percentages with a base below this are rendered greyed with «n=X»; deltas need ≥ TINY_N prior records. */
export const TINY_N = 5;

/** Launch of the worker quick report; ranges starting before it default to «Incluir histórico» ON. */
export const REPORTS_START_DATE = "2026-06-01";

/** Maximum buckets a phone chart should show (see `buckets.ts`). */
export const MAX_CHART_BUCKETS = 13;

/** Ranges up to this many days bucket by ISO week; longer ranges bucket by month. */
export const WEEK_BUCKET_MAX_DAYS = 120;
